import { Controller } from '@hotwired/stimulus'

// Soft brand flash on Turbo visits + form submits.
// Shows a small E logo animation; hides when navigation/request finishes.
export default class extends Controller {
  static targets = ['panel']
  static values = {
    minMs: { type: Number, default: 420 },
    maxMs: { type: Number, default: 8000 }
  }

  connect () {
    this.visibleSince = Date.now()
    this.hideTimer = null
    this.maxTimer = null
    this.pending = 1

    this.onClick = (event) => this.handleClick(event)
    this.onSubmitStart = (event) => this.handleSubmitStart(event)
    this.onSubmitEnd = () => this.scheduleHide()
    this.onNativeSubmit = (event) => this.handleNativeSubmit(event)
    this.onBeforeVisit = () => this.handleBeforeVisit()
    this.onLoad = () => this.scheduleHide()
    this.onFrameMissing = () => this.scheduleHide()
    this.onFetchRequestError = () => this.scheduleHide()

    document.addEventListener('turbo:click', this.onClick)
    document.addEventListener('turbo:submit-start', this.onSubmitStart)
    document.addEventListener('turbo:submit-end', this.onSubmitEnd)
    document.addEventListener('submit', this.onNativeSubmit, true)
    document.addEventListener('turbo:before-visit', this.onBeforeVisit)
    document.addEventListener('turbo:load', this.onLoad)
    document.addEventListener('turbo:frame-missing', this.onFrameMissing)
    document.addEventListener('turbo:fetch-request-error', this.onFetchRequestError)

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.forceHide()
      return
    }

    // First paint / hard refresh: panel starts visible, then soft-hide
    this.scheduleHide()
    this.maxTimer = window.setTimeout(() => this.forceHide(), this.maxMsValue)
  }

  disconnect () {
    document.removeEventListener('turbo:click', this.onClick)
    document.removeEventListener('turbo:submit-start', this.onSubmitStart)
    document.removeEventListener('turbo:submit-end', this.onSubmitEnd)
    document.removeEventListener('submit', this.onNativeSubmit, true)
    document.removeEventListener('turbo:before-visit', this.onBeforeVisit)
    document.removeEventListener('turbo:load', this.onLoad)
    document.removeEventListener('turbo:frame-missing', this.onFrameMissing)
    document.removeEventListener('turbo:fetch-request-error', this.onFetchRequestError)
    this.clearTimers()
  }

  handleClick (event) {
    const link = event.target.closest('a')
    if (!link) return
    if (event.defaultPrevented) return
    if (link.hasAttribute('download')) return
    if (link.target && link.target !== '_self') return
    if (link.dataset.pageLoader === 'skip' || link.closest('[data-controller~="load-more"]')) {
      this.skipNextVisit()
      return
    }
    const href = link.getAttribute('href')
    if (!href || href.startsWith('#')) return
    if (link.dataset.turbo === 'false' && !this.isSameOriginHref(href)) return

    this.show()
  }

  isSameOriginHref (href) {
    try {
      const url = new URL(href, window.location.origin)
      return url.origin === window.location.origin
    } catch (_) {
      return false
    }
  }

  handleBeforeVisit () {
    if (this.skipVisit) {
      this.skipVisit = false
      return
    }

    this.show()
  }

  skipNextVisit () {
    this.skipVisit = true
    window.clearTimeout(this.skipVisitTimer)
    this.skipVisitTimer = window.setTimeout(() => {
      this.skipVisit = false
      this.skipVisitTimer = null
    }, 0)
  }

  handleSubmitStart (event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    if (form.dataset.turbo === 'false') return
    if (this.skipForm(form)) return
    // Don't flash on live filter / search bars
    const controllers = form.dataset.controller || ''
    if (controllers.includes('filter-form') || controllers.includes('search-field')) return
    this.show()
  }

  handleNativeSubmit (event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    // Turbo forms are handled by turbo:submit-start
    if (form.dataset.turbo !== 'false') return
    if (this.skipForm(form)) return
    this.show()
  }

  skipForm (form) {
    const controllers = form.dataset.controller || ''

    return form.dataset.pageLoader === 'skip' ||
      form.id === 'message-form' ||
      controllers.includes('chat-compose')
  }

  show () {
    if (!this.hasPanelTarget) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    this.pending += 1
    this.visibleSince = this.visibleSince || Date.now()
    this.panelTarget.classList.add('is-visible')
    this.panelTarget.setAttribute('aria-hidden', 'false')
    document.documentElement.classList.add('page-loader-active')

    this.clearTimers()
    this.maxTimer = window.setTimeout(() => this.forceHide(), this.maxMsValue)
  }

  scheduleHide () {
    if (!this.hasPanelTarget) return
    if (this.pending > 0) this.pending -= 1
    if (this.pending > 0) return

    const elapsed = Date.now() - (this.visibleSince || Date.now())
    const wait = Math.max(0, this.minMsValue - elapsed)

    this.clearTimers()
    this.hideTimer = window.setTimeout(() => this.forceHide(), wait)
  }

  forceHide () {
    this.pending = 0
    this.visibleSince = 0
    this.clearTimers()
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove('is-visible')
    this.panelTarget.setAttribute('aria-hidden', 'true')
    document.documentElement.classList.remove('page-loader-active')
  }

  clearTimers () {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
    if (this.maxTimer) {
      clearTimeout(this.maxTimer)
      this.maxTimer = null
    }
    if (this.skipVisitTimer) {
      clearTimeout(this.skipVisitTimer)
      this.skipVisitTimer = null
    }
  }
}
